import type { PublicFont, PublicInkColor, TextNode } from '@repo/api-contracts';
import { TEXT_ALIGNS } from '@repo/api-contracts';
import { type ReactElement, useEffect, useRef } from 'react';
import { cn, Input, Label } from '@/shared/ui';
import { clampTo, TEXT_LIMITS } from '../model/text-limits';
import { RotationField } from './rotation-field';

interface TextPropertiesPanelProps {
  readonly node: TextNode;
  readonly fonts: readonly PublicFont[];
  readonly inks: readonly PublicInkColor[];
  readonly onChange: (patch: Partial<TextNode>) => void;
  readonly onDelete: () => void;
  /** 포커스 요청 순번 — 바뀔 때마다(텍스트 추가·캔버스 더블클릭) 내용 칸을 화면에 보이고 글자를 모두 고른다. */
  readonly focusSeq?: number | null;
}

/** 사이트 입력칸과 같은 결 — 한 줄 칸은 알약, 여러 줄 칸은 둥근 모서리(견적 요청 폼과 같다). */
const FIELD =
  'mt-1.5 w-full border border-input bg-background px-4 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const CHIP =
  'inline-flex h-8 items-center rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ALIGN_LABELS: Record<(typeof TEXT_ALIGNS)[number], string> = {
  left: '왼쪽 정렬',
  center: '가운데 정렬',
  right: '오른쪽 정렬',
};
const TOGGLES: readonly {
  key: 'bold' | 'italic' | 'underline' | 'strikethrough';
  label: string;
}[] = [
  { key: 'bold', label: '굵게' },
  { key: 'italic', label: '기울임' },
  { key: 'underline', label: '밑줄' },
  { key: 'strikethrough', label: '취소선' },
];

const numberOr = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const finiteOr = (value: string, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * 선택한 텍스트 노드의 폰트 스타일 — 내용 · 서체 선택 · 굵게/기울임/밑줄/취소선 · 정렬 · 글씨 색상 견본 ·
 * 크기/문자 간격/행 간격/회전. 값은 mm·em·° 단위이며 계약 범위로 맞춰 patch 로 올린다(높이 재계산은 호출자).
 */
export const TextPropertiesPanel = ({
  node,
  fonts,
  inks,
  onChange,
  onDelete,
  focusSeq = null,
}: TextPropertiesPanelProps): ReactElement => {
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const activeInk = inks.find((ink) => ink.id === node.inkColorId);
  useEffect(() => {
    const content = contentRef.current;
    if (focusSeq === null || content === null) return;
    if (typeof content.scrollIntoView === 'function')
      content.scrollIntoView({ block: 'center', behavior: 'smooth' });
    content.focus({ preventScroll: true });
    content.select();
  }, [focusSeq]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Label htmlFor="text-content">내용</Label>
        <textarea
          ref={contentRef}
          id="text-content"
          className={cn(FIELD, 'resize-none rounded-2xl py-2')}
          rows={2}
          maxLength={1000}
          placeholder="넣을 글자를 입력하세요"
          aria-describedby="text-content-hint"
          value={node.text}
          onChange={(e) => onChange({ text: e.target.value })}
        />
        <p id="text-content-hint" className="mt-1.5 text-xs text-muted-foreground">
          여기서 고친 글자가 상품 위에 바로 보여요. 캔버스의 글자를 더블클릭해도 이 칸으로 와요.
        </p>
      </div>
      <div>
        <Label htmlFor="text-font">서체 선택</Label>
        <select
          id="text-font"
          className={cn(FIELD, 'h-9 rounded-full')}
          value={node.fontId}
          onChange={(e) => onChange({ fontId: e.target.value })}
        >
          {fonts.map((font) => (
            <option key={font.id} value={font.id}>
              {font.familyName}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TOGGLES.map((toggle) => (
          <button
            key={toggle.key}
            type="button"
            aria-pressed={node[toggle.key]}
            className={cn(
              CHIP,
              node[toggle.key]
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-foreground/15 hover:border-foreground/40',
            )}
            onClick={() => onChange({ [toggle.key]: !node[toggle.key] })}
          >
            {toggle.label}
          </button>
        ))}
      </div>
      <fieldset>
        <legend className="text-sm font-medium">정렬</legend>
        <div className="mt-2 flex gap-1.5">
          {TEXT_ALIGNS.map((align) => (
            <label
              key={align}
              className={cn(
                CHIP,
                'cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                node.align === align
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-foreground/15 hover:border-foreground/40',
              )}
            >
              <input
                type="radio"
                name="align"
                aria-label={ALIGN_LABELS[align]}
                className="sr-only"
                checked={node.align === align}
                onChange={() => onChange({ align })}
              />
              {ALIGN_LABELS[align]}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-sm font-medium">
          글씨 색상
          {activeInk !== undefined && (
            <span className="font-normal text-muted-foreground"> · {activeInk.name}</span>
          )}
        </legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {inks.map((ink) => (
            <label
              key={ink.id}
              title={ink.name}
              className={cn(
                'flex size-9 cursor-pointer items-center justify-center rounded-full border-2 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                node.inkColorId === ink.id
                  ? 'border-foreground'
                  : 'border-transparent hover:border-foreground/30',
              )}
            >
              <input
                type="radio"
                name="ink"
                aria-label={ink.name}
                className="sr-only"
                checked={node.inkColorId === ink.id}
                onChange={() => onChange({ inkColorId: ink.id })}
              />
              <span
                aria-hidden
                className="inline-block size-7 rounded-full border border-foreground/20"
                style={{ backgroundColor: ink.colorCode }}
              />
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid grid-cols-2 gap-x-2 gap-y-3 sm:grid-cols-4">
        <div>
          <Label htmlFor="text-size" className="text-xs">
            크기(mm)
          </Label>
          <Input
            id="text-size"
            type="number"
            className="mt-1.5 px-3"
            min={TEXT_LIMITS.fontSizeMm.min}
            max={TEXT_LIMITS.fontSizeMm.max}
            step={0.5}
            value={node.fontSizeMm}
            onChange={(e) =>
              onChange({
                fontSizeMm: clampTo(
                  numberOr(e.target.value, node.fontSizeMm),
                  TEXT_LIMITS.fontSizeMm,
                ),
              })
            }
          />
        </div>
        <div>
          <Label htmlFor="text-spacing" className="text-xs">
            문자 간격(em)
          </Label>
          <Input
            id="text-spacing"
            type="number"
            className="mt-1.5 px-3"
            min={TEXT_LIMITS.letterSpacingEm.min}
            max={TEXT_LIMITS.letterSpacingEm.max}
            step={0.05}
            value={node.letterSpacingEm}
            onChange={(e) =>
              onChange({
                letterSpacingEm: clampTo(finiteOr(e.target.value, 0), TEXT_LIMITS.letterSpacingEm),
              })
            }
          />
        </div>
        <div>
          <Label htmlFor="text-line" className="text-xs">
            행 간격
          </Label>
          <Input
            id="text-line"
            type="number"
            className="mt-1.5 px-3"
            min={TEXT_LIMITS.lineHeight.min}
            max={TEXT_LIMITS.lineHeight.max}
            step={0.1}
            value={node.lineHeight}
            onChange={(e) =>
              onChange({
                lineHeight: clampTo(
                  numberOr(e.target.value, node.lineHeight),
                  TEXT_LIMITS.lineHeight,
                ),
              })
            }
          />
        </div>
        <RotationField
          value={node.rotationDeg}
          onChange={(rotationDeg) => onChange({ rotationDeg })}
        />
      </div>
      <button
        type="button"
        className="self-start text-xs text-muted-foreground underline-offset-4 hover:text-destructive hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onDelete}
      >
        텍스트 삭제
      </button>
    </div>
  );
};
